import type { User, Word, Taxon, SignVideo } from '@kys/shared-types';
export interface Tokens { accessToken:string; refreshToken:string; expiresIn:number; tokenType:string; sessionId:string }
export interface TokenStore { read():Promise<Tokens|null>; write(tokens:Tokens|null):Promise<void> }
export interface ApiResponse<T> { data:T; meta:unknown; error:null|{code:string;message?:string} }
export class ApiError extends Error {
 constructor(public code:string,public status:number){super(code.toLowerCase());}
}
interface Options { fetch?:typeof fetch; headers?:Record<string,string>; tokens?:TokenStore; timeoutMs?:number }
export class ApiClient {
 private refreshing?:Promise<void>;
 constructor(private baseUrl:string,private options:Options={}){}
 async request<T>(path:string,body?:unknown,retry=true):Promise<T>{
  const tokens=await this.options.tokens?.read();
  const headers:Record<string,string>={...this.options.headers};
  if(tokens)headers.Authorization=`Bearer ${tokens.accessToken}`;
  const form=typeof FormData!=='undefined'&&body instanceof FormData;
  if(body!==undefined&&!form)headers['Content-Type']='application/json';
  const controller=new AbortController();
  const timer=setTimeout(()=>controller.abort(),this.options.timeoutMs??15000);
  let response:Response;
  let value:ApiResponse<T>;
  try {
  response=await (this.options.fetch||fetch)(`${this.baseUrl.replace(/\/$/,'')}/${path.replace(/^\//,'')}`,{
   method:body===undefined?'GET':'POST',headers,credentials:'include',cache:'no-store',
   signal:controller.signal,
   ...(body===undefined?{}:{body:form?body as FormData:JSON.stringify(body)}),
  });
  value=await response.json().catch(()=>{throw new ApiError('UNAVAILABLE',response.status);}) as ApiResponse<T>;
  } catch(error) {
   if(error instanceof ApiError)throw error;
   throw new ApiError(controller.signal.aborted?'TIMEOUT':'UNAVAILABLE',0);
  } finally {clearTimeout(timer);}
  if(response.status===401&&tokens&&retry&&!path.startsWith('auth/')){
   const current=await this.options.tokens!.read();
   if(!current)throw new ApiError('UNAUTHORIZED',401);
   if(current.accessToken===tokens.accessToken){
    if(!this.refreshing)this.refreshing=this.refreshTokens(current.refreshToken).finally(()=>{this.refreshing=undefined;});
    await this.refreshing;
   }
   return this.request<T>(path,body,false);
  }
  if(!response.ok)throw new ApiError(value?.error?.code||'UNAVAILABLE',response.status);
  if(!value||typeof value!=='object'||!('data' in value))throw new ApiError('UNAVAILABLE',response.status);
  return value.data;
 }
 private async refreshTokens(refreshToken:string){
  try {
   const next=await this.request<Tokens>('auth/refresh',{refreshToken},false);
   if((await this.options.tokens!.read())?.refreshToken===refreshToken)await this.options.tokens!.write(next);
  }
  catch(error){if(error instanceof ApiError&&error.status===401&&(await this.options.tokens!.read())?.refreshToken===refreshToken)await this.options.tokens!.write(null);throw error;}
 }
 bootstrap=()=>this.request<{user:User|null;languages:Taxon[];categories:Taxon[];providers:Record<string,boolean>;turnstile:string|null;development:boolean}>('bootstrap');
 auth={
  me:()=>this.request<User>('auth/me'),
  login:(credentials:unknown)=>this.request<{ok:boolean}>('auth/login',credentials),
  register:(credentials:unknown)=>this.request<{ok:boolean}>('auth/register',credentials),
  mobileLogin:async(credentials:unknown)=>{const result=await this.request<Tokens>('auth/mobile/login',credentials);await this.options.tokens?.write(result);return result;},
  logout:async(all=false)=>{await this.request('auth/logout',{all});await this.options.tokens?.write(null);},
 };
 signs={search:(params:Record<string,string>={})=>this.request<Word[]>(`signs?${new URLSearchParams(params)}`),get:(id:string)=>this.request<{word:Word;videos:SignVideo[]}>(`signs/${encodeURIComponent(id)}`)};
 notifications={list:()=>this.request<unknown[]>('notifications')};
 favorites={list:(page=0)=>this.request<Word[]>(`favorites?page=${page}`),contains:(wordId:string)=>this.request<Word[]>(`favorites?word_id=${encodeURIComponent(wordId)}`).then(words=>words.some(word=>word.id===wordId)),set:(wordId:string,saved:boolean)=>this.request<{ok:boolean}>('favorites',{word_id:wordId,saved})};
 variations={list:(wordId:string)=>this.request<{word:Word;videos:SignVideo[]}>(`variations/${encodeURIComponent(wordId)}`).then(value=>value.videos)};
 videos={like:(videoId:string,liked:boolean)=>this.request<{ok:boolean}>('likes',{video_id:videoId,liked})};
 uploads={create:(form:FormData)=>this.request('uploads',form),initiate:(metadata:unknown)=>this.request('uploads/initiate',metadata),complete:(id:string)=>this.request('uploads/complete',{id})};
}
